type County = {
  name: string;
  nuts_region: string;
};

type Region = {
  name: string;
  nameCode: string;
  counties: County[];
};

type ApiResponse = {
  data: Region[];
  message: string;
};

const joiningWords = ['of', 'the', 'in', 'and', 'or', 'but', 'on', 'at', 'to', 'by', 'for', 'with', 'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'from', 'up', 'down', 'in', 'out', 'on', 'off', 'over', 'under', 'again', 'further', 'then', 'once'];

export const useRegionStore = defineStore('region', () => {
  const regions = ref<Region[]>([]);
  const currentLocationSlug = ref<string | null>(null);
  const popularSelected = ref(true);

  const currentRegion = computed(() => {
    if (isRegionBySlug(currentLocationSlug.value)) {
      return currentLocationSlug.value?.replace(/-/g, ' ')
        .split(' ')
        .map(word => capitalizeWords(word))
        .join(' ');
    }
    return findRegionByCountySlug(currentLocationSlug.value);
  });

  const currentCounty = computed(() => {
    if (isCountyBySlug(currentLocationSlug.value)) {
      return currentLocationSlug.value?.replace(/-/g, ' ')
        .split(' ')
        .map(word => capitalizeWords(word))
        .join(' ');
    }
    return null;
  });

  function capitalizeWords(name: string): string {
    return name
      .split(' ')
      .map((word, index) => {
        if (index === 0 || !joiningWords.includes(word.toLowerCase())) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word.toLowerCase();
      })
      .join(' ');
  }

  async function fetchRegions(): Promise<Region[]> {
    const { error, data } = await useFetch<ApiResponse>(
      useJustHireUrl('/api/regions'),
      {
        headers: useJustHireHeaders(),
      }
    );

    if (error.value) {
      useHandleJHError(error);
    }

    const regionsData = useKeysToCamel(data.value?.data);
    regions.value = regionsData;

    return regionsData;
  }

  function setCurrentLocationSlug(locationName: string) {
    currentLocationSlug.value = locationName?.toLowerCase().replace(/ /g, '-');
  }

  function getCountiesByRegion(regionName?: string): County[] {
    if (!regionName && regions.value.length > 0) {
      return regions.value[0].counties;
    }
    const region = regions.value.find(r => r.name.toLowerCase() === regionName?.toLowerCase());
    return region?.counties || [];
  }

  function getCountiesByRegionSlug(regionSlug?: string): County[] {
    if (!regionSlug) return [];
    const formattedRegionName = regionSlug
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalizeWords(word))
      .join(' ');
    return getCountiesByRegion(formattedRegionName);
  }

  function findRegionByCountySlug(countySlug: string): string | null {
    const county = getAllCounties().find(c => c.nameCode.toLowerCase() === countySlug.toLowerCase());
    if (county) {
      const region = regions.value.find(r => r.counties.some(c => c.nameCode.toLowerCase() === county.nameCode.toLowerCase()));
      return region ? region.name : null;
    }
    return null;
  }

  function getAllCounties(): County[] {
    return regions.value.flatMap(region => region.counties);
  }

  function isRegionBySlug(locationSlug: string): boolean {
    const formattedRegionName = locationSlug
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalizeWords(word))
      .join(' ');
    const regionNames = regions.value.map(region => region.name.toLowerCase());
    return regionNames.includes(formattedRegionName.toLowerCase());
  }

  function isCountyBySlug(locationSlug: string): boolean {
    const formattedCountyName = locationSlug
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalizeWords(word))
      .join(' ');
    return getAllCounties().some(county => county.name.toLowerCase() === formattedCountyName.toLowerCase());
  }

  function isRegion(): boolean {
    return isRegionBySlug(currentLocationSlug.value);
  }

  function isCounty(): boolean {
    return isCountyBySlug(currentLocationSlug.value);
  }

  function isPopular(): boolean {
    return currentLocationSlug.value === 'popular';
  }

  function isActiveGroup(groupName: string): boolean {
    if (groupName.toLowerCase() === 'popular') {
      return isPopular();
    }

    if (isPopular()) {
      return false;
    }

    const formattedGroupName = groupName.toLowerCase().replace(/ /g, '-');

    if (isRegion()) {
      return currentRegion.value?.toLowerCase() === formattedGroupName;
    }
    
    if (!currentLocationSlug.value) return false;
    const regionName = findRegionByCountySlug(currentLocationSlug.value);
    return regionName?.toLowerCase().replace(/ /g, '-') === formattedGroupName;
  }

  const currentLocation = computed(() => {
    return currentLocationSlug.value?.replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalizeWords(word))
      .join(' ');
  });

  return {
    regions,
    currentLocationSlug,
    capitalizeWords,
    fetchRegions,
    setCurrentLocationSlug,
    getCountiesByRegion,
    getCountiesByRegionSlug,
    getAllCounties,
    findRegionByCountySlug,
    isRegionBySlug,
    isActiveGroup,
    currentLocation,
    currentRegion,
    currentCounty,
    isRegion,
    isCounty,
    isPopular,
  };
}); 